import { apiClient } from "@lib/axios";
import type { TAccount } from "./types";

export const accountApi = {
  // READ (GET)
  getAccount: async () => {
    return await apiClient.get<TAccount>("/accounts/info");
  },
  getInvitationLink: async () => {
    return await apiClient.get<TAccount>("/accounts/get-invitation-link");
  },
  getToken: async () => {
    return await apiClient.get<TAccount>("/accounts/token");
  },
  getInvoices: async () => {
    return await apiClient.get<TAccount>("/accounts/getInvoices");
  },
  getCancelationDiscount: async () => {
    return await apiClient.get<TAccount>(
      "/accounts/apply-cancellation-discount",
    );
  },
  verifyClaimDiscountEligibility: async () => {
    return await apiClient.get<TAccount>(
      "/accounts/verify-cancellation-discount-eligibility",
    );
  },

  // CREATE (POST)
  searchAccounts: async (search: string) => {
    return await apiClient.post<TAccount[]>("/accounts/search", {
      search,
      admin: true,
    });
  },
  addSeats: async (seats: number, description: string) => {
    return await apiClient.post<TAccount>("/accounts/addSeats", {
      seats,
      description,
    });
  },
  buySeats: async (seats: number) => {
    return await apiClient.post<TAccount>("/accounts/buySeats", { seats });
  },
  handlePlanProperties: async (freeSeats: number) => {
    return await apiClient.post<TAccount>("/accounts/plan-properties", {
      freeSeats,
    });
  },
  enableCollaboration: async () => {
    return await apiClient.post<TAccount>("/accounts/enable-collaboration");
  },
  disconnectCustomDomain: async () => {
    return await apiClient.post<void>("/accounts/disconnect-custom-domain");
  },
  sendVerificationEmail: async () => {
    return await apiClient.post<TAccount>("/accounts/sendVerificationEmail");
  },
  updateAccountProperties: async (
    type: string,
    payload: Record<string, unknown>,
    id: string,
  ) => {
    return await apiClient.post<TAccount>("/accounts/account-properties", {
      type,
      payload,
      id,
    });
  },
  updateAccountSettings: async (payload: Record<string, unknown>) => {
    return await apiClient.post<TAccount>("/accounts/account-settings", {
      payload,
    });
  },
  updateDefaultTheme: async (id: string) => {
    return await apiClient.post<TAccount>("/accounts/update-default-theme", {
      id,
    });
  },
  updateSelectedFonts: async (fonts: { title: string; value: string }[]) => {
    return await apiClient.post<TAccount>("/accounts/predefined-fonts", {
      fonts,
    });
  },
  cancelSubscription: async () => {
    return await apiClient.post<TAccount>("/accounts/cancelPlan");
  },
  updatePlan: async (planId: string, discount: boolean) => {
    return await apiClient.post<TAccount>("/accounts/updatePlan", {
      planId,
      discount,
    });
  },
  updateAccountCustomization: async (body: Record<string, unknown>) => {
    return await apiClient.post<TAccount>("/accounts/customization", body);
  },
  updateSubscriptionLimitations: async (data: Record<string, unknown>) => {
    return await apiClient.post<TAccount>(
      "/accounts/update-subscription-limits",
      { data },
    );
  },
  upgradePlan: async (planId: string) => {
    return await apiClient.post<TAccount>("/accounts/updatePlan", { planId });
  },
  updateTimestamp(key: string) {
    return apiClient.post("/accounts/timestamp", { key });
  },
  trackEvent: async (event: string, payload: Record<string, unknown>) => {
    return await apiClient.post<TAccount>("/accounts/mixpanel-event", {
      event,
      payload,
    });
  },
  postTrialVisitDate: async () => {
    return await apiClient.post<TAccount>("/accounts/postTrialVisit");
  },
  setMobileOnboardingStarted: async (payload: {
    doc_type: string;
    subtype: string;
    preview_gif: string;
    story_id: string;
  }) => {
    return await apiClient.post<TAccount>(
      "/accounts/mobile-onboarding-started",
      { payload },
    );
  },
  setMobileOnboardingCompleted: async () => {
    return await apiClient.post<TAccount>(
      "/accounts/mobile-onboarding-finished",
    );
  },
  setMobileDocpilotOnboardingCompleted: async () => {
    return await apiClient.post<TAccount>(
      "/accounts/mobile-docpilot-onboarding-finished",
    );
  },

  setFirstTimeLogin: async () => {
    return await apiClient.post<TAccount>("/accounts/firstLogin");
  },

  // UPDATE (PUT)
  updateInvitationLink: async (status: "active" | "inactive") => {
    return await apiClient.put<TAccount>("/accounts/update-invitation-link", {
      status,
    });
  },

  // DELETE
  deleteAccount: async () => {
    return await apiClient.delete<TAccount>("/accounts");
  },
};
